import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, Upload, ArrowLeft, Loader2, FileText, Award, CheckCircle2, AlertCircle, Lightbulb, BookOpen, Clock, ChevronRight, Timer, AlertTriangle, Home, Sparkles, Lock, ShoppingCart, ExternalLink, RefreshCw, X, Camera, Download } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
// Topics fetched from LMS Manager API
import FloatingBackButton from "@/components/FloatingBackButton";
import PremiumPopup from "@/components/PremiumPopup";
import MathText from "@/components/MathText";
import DescriptiveFeedback from "@/components/DescriptiveFeedback";
import { downloadQuestionSheet } from "@/utils/downloadSheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Level badge component with color coding
const LevelBadge = ({ level }) => {
  const colors = {
    LOW: "bg-red-100 text-red-700 border-red-200",
    MODERATE: "bg-yellow-100 text-yellow-700 border-yellow-200",
    HIGH: "bg-green-100 text-green-700 border-green-200"
  };
  
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${colors[level] || colors.MODERATE}`}>
      {level}
    </span>
  );
};

// Feedback Card Component
const FeedbackCard = ({ feedback, timePenaltyApplied }) => {
  if (!feedback || typeof feedback !== 'object') {
    return <p className="text-gray-500">No feedback available</p>;
  }

  return (
    <div className="space-y-4">
      {/* Time Penalty Warning */}
      {timePenaltyApplied && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-bold">Time Penalty Applied: -1 Mark</span>
          </div>
          <p className="text-sm text-red-600 mt-1">You exceeded the 8-minute time limit</p>
        </div>
      )}

      {/* Clarity */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-blue-600" />
            Clarity in Your Answer
          </h3>
          <LevelBadge level={feedback.clarity} />
        </div>
        <p className="text-sm text-gray-600">How clear and well-structured your answer is</p>
      </div>

      {/* Factual Base */}
      <div className="bg-gradient-to-br from-purple-50 to-violet-50 border-2 border-purple-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-purple-600" />
            Factual Base in Your Answer
          </h3>
          <LevelBadge level={feedback.factual} />
        </div>
        <p className="text-sm text-gray-600">Correctness of facts and concepts used</p>
      </div>

      {/* What You Got Right — Good Highlights */}
      {(feedback.good_highlights || []).length > 0 && (
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-xl p-5">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            What You Got Right
          </h3>
          <div className="space-y-3">
            {feedback.good_highlights.map((h, i) => (
              <div key={i} className="border-l-4 border-emerald-400 pl-3 py-1">
                {h.quote && (
                  <p className="text-sm italic text-emerald-800 mb-1">"{h.quote}"</p>
                )}
                <p className="text-sm text-emerald-700">{h.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Improvement Needed — Improvement Highlights */}
      {(feedback.improvement_highlights || []).length > 0 && (
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-5">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Improvement Needed
          </h3>
          <div className="space-y-3">
            {feedback.improvement_highlights.map((h, i) => (
              <div key={i} className="border-l-4 border-amber-400 pl-3 py-1">
                {h.quote && (
                  <p className="text-sm italic text-amber-800 mb-1">"{h.quote}"</p>
                )}
                <p className="text-sm text-amber-700">{h.suggestion}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* What's Missing */}
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-5">
        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-3">
          <AlertCircle className="w-5 h-5 text-orange-600" />
          What's Missing in Your Answer
        </h3>        <div className="text-gray-700 leading-relaxed">
          <MathText text={feedback.missing || "No specific gaps identified"} />
        </div>
      </div>

      {/* Ideal CBSE Answer */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-5">
        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-3">
          <Lightbulb className="w-5 h-5 text-green-600" />
          How to Write a 4-Mark CBSE Answer
        </h3>
        <div className="text-gray-700 leading-relaxed space-y-1">
          {(feedback.ideal_answer || "Model answer not available")
            .replace(/\*\*/g, '')
            .split('\n')
            .map((line, i) => (
              <div key={i} className={line.trim() === '' ? 'h-2' : ''}>
                {line.trim() !== '' && <MathText text={line} />}
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};

// TGT Subjects
const tgtSubjects = [
  { value: "hindi", label: "Hindi" },
  { value: "english", label: "English" },
  { value: "mathematics", label: "Mathematics" },
  { value: "science", label: "Science" },
  { value: "social_science", label: "Social Science" },
  { value: "computer_science", label: "Computer Science" },
  { value: "physical_education", label: "Physical Education" },
  { value: "music", label: "Music" },
  { value: "art", label: "Art/Drawing" }
];

// PGT Subjects
const pgtSubjects = [
  { value: "hindi", label: "Hindi" },
  { value: "english", label: "English" },
  { value: "mathematics", label: "Mathematics" },
  { value: "physics", label: "Physics" },
  { value: "chemistry", label: "Chemistry" },
  { value: "biology", label: "Biology" },
  { value: "commerce", label: "Commerce" },
  { value: "economics", label: "Economics" },
  { value: "geography", label: "Geography" },
  { value: "history", label: "History" },
  { value: "computer_science", label: "Computer Science" },
  { value: "music", label: "Music" },
  { value: "art", label: "Art/Fine Arts" }
];

// Non-Teaching Subjects
const nonTeachingSubjects = [
  { value: "hostel_warden", label: "Hostel Warden" },
  { value: "library_science", label: "Library Science" }
];

// Topics fetched dynamically from LMS Manager API

export default function Evaluate({ user, onLogout, subjectSelection }) {
  const [searchParams] = useSearchParams();
  // For all-subjects exams (KVS PRT), subject='all' means browse subjects first
  const isAllSubjects = subjectSelection?.subject === 'all';
  // Step tracking: 1=exam type, 2=subject, 3=topic, 4=question loading, 5=answer & timer, 6=evaluating, 7=result
  const [step, setStep] = useState(() => {
    if (!subjectSelection) return 1;
    if (isAllSubjects) return 2; // KVS PRT: pick subject first
    return 3;
  });
  const [examType, setExamType] = useState(() => subjectSelection?.category || "");
  const [subject, setSubject] = useState(() => (subjectSelection?.subject && subjectSelection.subject !== 'all') ? subjectSelection.subject : "");
  const [topic, setTopic] = useState("");
  const [question, setQuestion] = useState("");
  const [idealAnswer, setIdealAnswer] = useState("");
  const [questionLoading, setQuestionLoading] = useState(false);

  // Safety net: if question is stored as raw JSON string (parse failure bug), extract just the question text
  const extractQuestionText = (q) => {
    if (!q || typeof q !== 'string') return q;
    const trimmed = q.trim();
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        return parsed.question || q;
      } catch {
        // Regex fallback for malformed JSON with literal newlines
        const match = trimmed.match(/"question"\s*:\s*"([\s\S]*?)(?:",\s*"ideal_answer"|"\s*\})/);
        if (match) return match[1].replace(/\\n/g, '\n').trim();
      }
    }
    return q;
  };
  
  // Timer state
  const [timeLeft, setTimeLeft] = useState(480);
  const [timerActive, setTimerActive] = useState(false);
  const [timeExceeded, setTimeExceeded] = useState(false);

  // Auto-fill from URL params (coming from Notes page)
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current) return;
    const et = searchParams.get('exam_type');
    const sub = searchParams.get('subject');
    const top = searchParams.get('topic');
    if (et && sub && top) {
      prefilled.current = true;
      setExamType(et);
      setSubject(sub);
      // Auto-trigger question generation
      setTopic(top);
      setStep(4);
      setQuestionLoading(true);
      axios.post(`${API}/generate-question`, {
        exam_type: et, subject: sub, topic: top, user_id: user.id
      }).then(res => {
        setQuestion(extractQuestionText(res.data.question));
        setIdealAnswer(res.data.ideal_answer || "");
        setStep(5);
        setTimeLeft(480);
        setTimerActive(true);
        setTimeExceeded(false);
      }).catch(() => {
        toast.error("Failed to generate question. Please try again.");
        setStep(subjectSelection ? 3 : 1);
      }).finally(() => setQuestionLoading(false));
    }
  }, [searchParams, subjectSelection]);
  const timerRef = useRef(null);
  
  // Upload and evaluation state — 1 page slot only
  const [pages, setPages] = useState([null]);       // [page1File]
  const [pagePreviews, setPagePreviews] = useState([null]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [shuffling, setShuffling] = useState(false); // For shuffle button loading
  
  // Facts state for engaging loading screen
  const [currentFact, setCurrentFact] = useState("");
  const [facts, setFacts] = useState([]);
  const factIntervalRef = useRef(null);
  
  // Premium popup state
  const [showPremiumPopup, setShowPremiumPopup] = useState(false);

  // Dynamic topics from LMS Manager API
  const [currentTopics, setCurrentTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);

  // Topic progress tracking (descriptive section)
  const [coveredTopics, setCoveredTopics] = useState(new Set());

  const fetchTopicsFromAPI = useCallback(async (examTypeVal, subjectVal) => {
    if (!examTypeVal || !subjectVal) {
      setCurrentTopics([]);
      return;
    }
    setLoadingTopics(true);
    try {
      const res = await axios.get(`${API}/notes/lms/syllabus/exams/${examTypeVal}/subjects/${subjectVal}/topics`);
      const topics = res.data.topics || [];
      const formattedTopics = topics
        .filter(t => t && t !== '__placeholder__')
        .map(t => ({ value: t, label: t }));
      setCurrentTopics(formattedTopics);
    } catch (err) {
      console.error("Failed to fetch topics:", err);
      setCurrentTopics([]);
    } finally {
      setLoadingTopics(false);
    }
  }, []);

  // Fetch topics + coverage when exam type or subject changes
  useEffect(() => {
    if (examType && subject) {
      fetchTopicsFromAPI(examType, subject);
      // Fetch covered topics for descriptive section
      if (user?.id) {
        axios.get(`${API}/progress?user_id=${encodeURIComponent(user.id)}&exam_type=${encodeURIComponent(examType)}&subject=${encodeURIComponent(subject)}&section=descriptive`)
          .then(r => setCoveredTopics(new Set(r.data.covered || [])))
          .catch(() => {});
      }
    } else {
      setCurrentTopics([]);
    }
  }, [examType, subject, fetchTopicsFromAPI]);

  // Timer effect
  useEffect(() => {
    if (timerActive && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setTimeExceeded(true);
            toast.warning("Time is over! Please submit your answer. 1 mark will be deducted.");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timerActive]);

  // Fetch facts on component mount
  useEffect(() => {
    const fetchFacts = async () => {
      try {
        const response = await axios.get(`${API}/facts/all`);
        if (response.data.success && response.data.facts.length > 0) {
          setFacts(response.data.facts);
          setCurrentFact(response.data.facts[Math.floor(Math.random() * response.data.facts.length)]);
        }
      } catch (error) {
        console.log("Facts not loaded");
      }
    };
    fetchFacts();
  }, []);

  // Rotate facts during loading (step 4 or step 6)
  useEffect(() => {
    if ((step === 4 || step === 6) && facts.length > 0) {
      factIntervalRef.current = setInterval(() => {
        setCurrentFact(facts[Math.floor(Math.random() * facts.length)]);
      }, 4000);
    }
    return () => {
      if (factIntervalRef.current) {
        clearInterval(factIntervalRef.current);
      }
    };
  }, [step, facts]);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle exam type selection
  const handleExamTypeSelect = (type) => {
    setExamType(type);
    setSubject("");
    setTopic("");
    setStep(2);
  };

  // Handle subject selection
  const handleSubjectSelect = (value) => {
    setSubject(value);
    setTopic("");
    setStep(3);
  };

  // Handle topic selection and generate question
  const handleTopicSelect = async (value, idx) => {
    // Paywall check: Only first topic is free for free users
    const isFreeUser = !user.plan || user.plan === 'free';
    const isLocked = isFreeUser && idx > 0;
    
    if (isLocked) {
      setShowPremiumPopup(true);
      return;
    }

    // Mark topic as covered in descriptive section
    if (user?.id) {
      axios.post(`${API}/progress/mark`, {
        user_id: user.id, exam_type: examType, subject, topic: value, section: "descriptive"
      }).then(() => setCoveredTopics(prev => new Set([...prev, value]))).catch(() => {});
    }
    
    setTopic(value);
    setStep(4);
    setQuestionLoading(true);
    
    try {
      const response = await axios.post(`${API}/generate-question`, {
        exam_type: examType,
        subject: subject,
        topic: value,
        user_id: user.id  // Pass user_id for question rotation
      });
      
      setQuestion(extractQuestionText(response.data.question));
      setIdealAnswer(response.data.ideal_answer || ""); // Store cached ideal answer
      setStep(5);
      setTimeLeft(480);
      setTimerActive(true);
      setTimeExceeded(false);
      setPages([null]); // Clear previous uploads
      setPagePreviews([null]);
    } catch (error) {
      toast.error("Failed to generate question. Please try again.");
      setStep(3);
    } finally {
      setQuestionLoading(false);
    }
  };
  
  // NEW: Handle shuffle - generate new question for same topic
  const handleShuffleQuestion = async () => {
    // Safety: don't allow shuffle if files are already uploaded
    if (pages.filter(Boolean).length > 0) {
      toast.error("Remove your uploaded files before changing the question.");
      return;
    }
    setShuffling(true);
    setPages([null]);
    setPagePreviews([null]);
    
    try {
      const response = await axios.post(`${API}/generate-question`, {
        exam_type: examType,
        subject: subject,
        topic: topic,
        user_id: user.id,
        force_new: true,
        current_question: question  // Exclude current question
      });
      
      setQuestion(extractQuestionText(response.data.question));
      setIdealAnswer(response.data.ideal_answer || "");
      setTimeLeft(480);
      setTimerActive(true);
      setTimeExceeded(false);
      toast.success("New question generated!");
    } catch (error) {
      toast.error("Failed to generate new question. Please try again.");
    } finally {
      setShuffling(false);
    }
  };

  // Handle change question - get a new question for the same topic
  const handleChangeQuestion = async () => {
    // Safety: don't allow change if files are already uploaded
    if (pages.filter(Boolean).length > 0) {
      toast.error("Remove your uploaded files before changing the question.");
      return;
    }
    setQuestionLoading(true);
    
    try {
      const response = await axios.post(`${API}/generate-question`, {
        exam_type: examType,
        subject: subject,
        topic: topic,
        user_id: user.id,
        force_new: true,
        current_question: question  // Exclude current question from pool
      });
      
      setQuestion(extractQuestionText(response.data.question));
      setIdealAnswer(response.data.ideal_answer || "");
      // Reset timer
      setTimeLeft(480);
      setTimerActive(true);
      setTimeExceeded(false);
      // Clear any uploaded files (reset page slot)
      setPages([null]);
      setPagePreviews([null]);
      toast.success("New question generated!");
    } catch (error) {
      toast.error("Failed to change question. Please try again.");
    } finally {
      setQuestionLoading(false);
    }
  };

  // Handle individual page upload
  const handlePageUpload = (file, pageIdx) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'];
    if (!validTypes.includes(file.type.toLowerCase())) {
      toast.error(`Invalid file type. Supported: JPG, PNG, WEBP, HEIC, PDF`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`File too large. Maximum 10MB per file`);
      return;
    }

    const newPages = [...pages];
    newPages[pageIdx] = file;
    setPages(newPages);

    // Generate preview
    if (file.type === 'application/pdf') {
      setPagePreviews(prev => { const p = [...prev]; p[pageIdx] = 'pdf'; return p; });
    } else {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPagePreviews(prev => { const p = [...prev]; p[pageIdx] = reader.result; return p; });
      };
      reader.readAsDataURL(file);
    }
    toast.success(`Page ${pageIdx + 1} uploaded!`);
  };

  // Remove a page
  const handleRemovePage = (pageIdx) => {
    const newPages = [...pages];
    newPages[pageIdx] = null;
    setPages(newPages);
    const newPreviews = [...pagePreviews];
    newPreviews[pageIdx] = null;
    setPagePreviews(newPreviews);
  };

  // Handle evaluation submission
  const handleSubmit = async () => {
    const filesToUpload = pages.filter(Boolean);
    if (filesToUpload.length === 0) {
      toast.error("Please upload at least Page 1 of your answer");
      return;
    }

    // Stop timer
    setTimerActive(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setStep(6);
    setLoading(true);

    const formData = new FormData();
    formData.append("user_id", user.id);
    formData.append("subject", `${examType}_${subject}`);
    formData.append("topic", topic);
    formData.append("question", question);
    formData.append("cbse_mode", "true");
    formData.append("time_exceeded", timeExceeded.toString());
    
    // Pass cached ideal answer to avoid re-generating it
    if (idealAnswer) {
      formData.append("stored_ideal_answer", idealAnswer);
    }
    
    // Append all non-null pages in order
    filesToUpload.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await axios.post(`${API}/evaluate`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setResult(response.data);
      setStep(7);
      toast.success("Answer evaluated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Evaluation failed");
      setStep(5);
      setTimerActive(true);
    } finally {
      setLoading(false);
    }
  };

  // Reset and start new question
  const handleNewQuestion = () => {
    setStep(1);
    setExamType("");
    setSubject("");
    setTopic("");
    setQuestion("");
    setIdealAnswer("");
    setPages([null]);
    setPagePreviews([null]);
    setResult(null);
    setTimeLeft(240);
    setTimerActive(false);
    setTimeExceeded(false);
  };

  const currentSubjects = examType === "tgt" ? tgtSubjects : examType === "pgt" ? pgtSubjects : nonTeachingSubjects;

  // Dynamic subjects from LMS API (for KVS PRT and other non-TGT/PGT exams)
  const [lmsSubjects, setLmsSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  useEffect(() => {
    if (!examType) { setLmsSubjects([]); return; }
    setLoadingSubjects(true);
    axios.get(`${API}/notes/lms/syllabus/exams/${examType}/subjects`)
      .then(res => setLmsSubjects(res.data.subjects || []))
      .catch(() => setLmsSubjects([]))
      .finally(() => setLoadingSubjects(false));
  }, [examType]);

  // Use LMS subjects for non-TGT/PGT exams, hardcoded for TGT/PGT/Non-Teaching
  const SUBJECT_SPECIFIC_EXAMS = ['tgt', 'pgt', 'non_teaching'];
  const useHardcodedSubjects = SUBJECT_SPECIFIC_EXAMS.includes(examType);
  const displaySubjects = useHardcodedSubjects
    ? currentSubjects.map(s => ({ id: s.value, name: s.label }))
    : lmsSubjects;
  const getSubjectLabel = () => displaySubjects.find(s => s.id === subject)?.name || subject;

  // Paywall guard for free users (after all hooks)
  const isFreeBlocked = user.plan === 'free' && user.questions_limit === 0 && user.questions_used >= 1; // Free users get 1 free
  const hasNoQuota = user.questions_limit !== -1 && user.questions_used >= user.questions_limit;
  if (isFreeBlocked || hasNoQuota) {
    const message = hasNoQuota ? "Your evaluations are exhausted!" : "Your 1 free descriptive is used!";
    const ctaText = hasNoQuota ? "Buy Top-up @ ₹50" : "TIER-2 PRO PLAN @ ₹499";
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-2 border-orange-300 bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 shadow-xl" data-testid="evaluate-paywall">
          <CardContent className="py-8 px-6 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">{message}</h2>
            <p className="text-gray-600 mb-2">To continue practicing descriptive questions</p>
            <p className="text-xl font-bold text-orange-700 mb-6">{ctaText}</p>
            <Link to="/pro" data-testid="evaluate-buy-btn">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-lg px-8 py-6 rounded-xl shadow-lg">
                <ShoppingCart className="w-5 h-5 mr-2" />
                Upgrade Now
              </Button>
            </Link>
            <div className="mt-6">
              <Link to="/dashboard">
                <Button variant="outline" size="sm">
                  <Home className="w-4 h-4 mr-2" />Back to Dashboard
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Link to="/dashboard" className="flex items-center gap-2 text-gray-600 hover:text-gray-800">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <GraduationCap className="w-8 h-8 text-blue-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                CBSE Descriptive Practice
              </span>
            </div>
            {step === 5 && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold ${timeExceeded ? 'bg-red-100 text-red-700' : timeLeft <= 60 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                <Timer className="w-5 h-5" />
                {formatTime(timeLeft)}
              </div>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {['Exam Type', 'Subject', 'Topic', 'Question', 'Submit'].map((label, idx) => (
              <div key={idx} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step > idx + 1 ? 'bg-green-500 text-white' : step === idx + 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {step > idx + 1 ? '✓' : idx + 1}
                </div>
                {idx < 4 && <div className={`w-8 md:w-16 h-1 ${step > idx + 1 ? 'bg-green-500' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Select Exam Type */}
        {step === 1 && (
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Select Exam Type</CardTitle>
              <p className="text-gray-600">Choose the category for your descriptive question</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  onClick={() => handleExamTypeSelect("tgt")}
                  className="h-32 text-lg bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800"
                  data-testid="select-tgt"
                >
                  <div className="text-center">
                    <GraduationCap className="w-10 h-10 mx-auto mb-2" />
                    <div className="font-bold">TGT</div>
                    <div className="text-sm opacity-90">Trained Graduate Teacher</div>
                    <div className="text-xs opacity-75 mt-1">Up to Class 10th Level</div>
                  </div>
                </Button>
                <Button
                  onClick={() => handleExamTypeSelect("pgt")}
                  className="h-32 text-lg bg-gradient-to-br from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800"
                  data-testid="select-pgt"
                >
                  <div className="text-center">
                    <Award className="w-10 h-10 mx-auto mb-2" />
                    <div className="font-bold">PGT</div>
                    <div className="text-sm opacity-90">Post Graduate Teacher</div>
                    <div className="text-xs opacity-75 mt-1">Class 11th-12th Level</div>
                  </div>
                </Button>
                <Button
                  onClick={() => handleExamTypeSelect("non_teaching")}
                  className="h-32 text-lg bg-gradient-to-br from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700"
                  data-testid="select-non-teaching"
                >
                  <div className="text-center">
                    <Home className="w-10 h-10 mx-auto mb-2" />
                    <div className="font-bold">Non-Teaching</div>
                    <div className="text-sm opacity-90">Hostel Warden & Others</div>
                    <div className="text-xs opacity-75 mt-1">Administrative Staff</div>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Subject */}
        {step === 2 && (
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Badge className={examType === "tgt" ? "bg-blue-600" : examType === "pgt" ? "bg-purple-600" : "bg-amber-600"}>
                  {examType.replace(/_/g, ' ').toUpperCase()}
                </Badge>
                {isAllSubjects && <Badge className="bg-green-600">All Subjects</Badge>}
              </div>
              <CardTitle className="text-2xl">Select Subject</CardTitle>
              <p className="text-gray-600">Choose your subject for the descriptive question</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {loadingSubjects ? (
                  <div className="col-span-3 py-6 text-center text-gray-400">Loading subjects...</div>
                ) : displaySubjects.length === 0 ? (
                  <div className="col-span-3 py-6 text-center text-gray-400">No subjects found.</div>
                ) : (
                  displaySubjects.map((subj) => (
                    <Button
                      key={subj.id}
                      variant="outline"
                      onClick={() => handleSubjectSelect(subj.id)}
                      className="h-16 hover:bg-blue-50 hover:border-blue-400"
                      data-testid={`select-subject-${subj.id}`}
                    >
                      <span className="font-semibold">{subj.name}</span>
                    </Button>
                  ))
                )}
              </div>
              {!isAllSubjects && (
                <Button variant="ghost" onClick={() => setStep(1)} className="mt-4">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Select Topic */}
        {step === 3 && (
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Badge className="bg-amber-600">{examType.replace(/_/g, ' ').toUpperCase()}</Badge>
                <Badge variant="outline">{getSubjectLabel()}</Badge>
              </div>
              <CardTitle className="text-2xl">Select Topic</CardTitle>
              <p className="text-gray-600">Choose a topic from the syllabus</p>
            </CardHeader>
            <CardContent>
              {loadingTopics ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Loading topics...</p>
                </div>
              ) : currentTopics.length > 0 ? (
                <div className="space-y-2">
                  {currentTopics.map((t, idx) => {
                    const isFreeUser = !user.plan || user.plan === 'free';
                    const isLocked = isFreeUser && idx > 0;
                    const isFree = idx === 0;
                    const isCovered = coveredTopics.has(t.value);
                    const glowStyle = isCovered
                      ? { outline: '2px solid #22c55e', boxShadow: '0 0 14px rgba(34,197,94,0.35)' }
                      : { outline: '1.5px solid rgba(239,68,68,0.45)', boxShadow: '0 0 8px rgba(239,68,68,0.18)' };
                    return (
                      <Button
                        key={idx}
                        variant="outline"
                        style={glowStyle}
                        onClick={() => handleTopicSelect(t.value, idx)}
                        className={`w-full justify-between h-auto py-3 px-4 text-left ${isLocked ? 'opacity-70 hover:bg-orange-50' : 'hover:bg-blue-50'}`}
                        data-testid={`select-topic-${idx}`}
                      >
                        <div className="flex items-center">
                          <ChevronRight className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span>{t.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isCovered && <span className="text-[10px] font-bold text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">Covered</span>}
                          {isFree && !isCovered && <Badge className="bg-green-100 text-green-700 text-xs">Free</Badge>}
                          {isLocked && <Lock className="w-4 h-4 text-orange-500" />}
                        </div>
                      </Button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-500">No topics available for this subject</p>
              )}
              
              {/* Paywall info for free users */}
              {(!user.plan || user.plan === 'free') && currentTopics.length > 1 && (
                <Card className="mt-4 border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
                  <CardContent className="py-4 text-center">
                    <Lock className="w-6 h-6 mx-auto mb-2 text-orange-500" />
                    <p className="text-sm text-gray-600">First topic is free! Unlock all topics with</p>
                    <p className="font-bold text-orange-700">Premium Plan @ ₹499</p>
                  </CardContent>
                </Card>
              )}
              <Button variant="ghost" onClick={() => { setSubject(""); setStep(2); }} className="mt-4">
                <ArrowLeft className="w-4 h-4 mr-2" /> {isAllSubjects ? "Browse Other Subjects" : "Back"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Question Loading with Interesting Facts */}
        {step === 4 && (
          <Card className="shadow-xl overflow-hidden">
            <CardContent className="py-12 text-center">
              <div className="mb-6">
                <div className="relative inline-block">
                  <Loader2 className="w-16 h-16 animate-spin text-blue-600 mx-auto" />
                  <Sparkles className="w-6 h-6 text-yellow-500 absolute -top-1 -right-1 animate-pulse" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Generating Your Question...</h2>
              <p className="text-gray-600 mb-4">CBSE Expert Team is preparing a descriptive question for you</p>
              <div className="flex items-center justify-center gap-2 mb-6">
                <Badge className={examType === "tgt" ? "bg-blue-600" : examType === "pgt" ? "bg-purple-600" : "bg-orange-600"}>
                  {examType.toUpperCase()}
                </Badge>
                <Badge variant="outline">{getSubjectLabel()}</Badge>
                <Badge variant="outline">{topic}</Badge>
              </div>
              
              {/* Interesting Fact Display */}
              {currentFact && (
                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-xl p-5 mx-auto max-w-lg mt-6">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-left">
                      <p className="text-sm font-semibold text-amber-700 mb-1">Did You Know?</p>
                      <p className="text-gray-700 text-sm leading-relaxed">{currentFact}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 5: Question Display with Timer and Upload */}
        {step === 5 && (
          <div className="space-y-6">
            {/* Time Warning */}
            {timeExceeded && (
              <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                <div>
                  <p className="font-bold text-red-700">Time is Over!</p>
                  <p className="text-sm text-red-600">Please submit your answer. 1 mark will be deducted for exceeding time.</p>
                </div>
              </div>
            )}

            {/* Question Card */}
            <Card className="shadow-xl border-2 border-blue-200">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-white/20">{examType.toUpperCase()}</Badge>
                    <Badge className="bg-white/20">{getSubjectLabel()}</Badge>
                  </div>
                  <Badge className="bg-yellow-500 text-yellow-900">4 Marks</Badge>
                </div>
                <p className="text-sm text-blue-100 mt-2">Topic: {topic}</p>
              </CardHeader>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-800 leading-relaxed"><MathText text={question} /></h3>
                
                {/* Change Question & Download Sheet */}
                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-3 flex-wrap">
                  {pages.filter(Boolean).length > 0 ? (
                    <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex-1">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      <span>Question locked after file upload. Remove your file(s) to change the question.</span>
                    </div>
                  ) : (
                    <Button 
                      variant="outline" 
                      onClick={handleChangeQuestion}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      disabled={questionLoading}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${questionLoading ? 'animate-spin' : ''}`} />
                      Change Question
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      toast.info("Generating PDF...");
                      downloadQuestionSheet(question).then(() => toast.success("Sheet downloaded!")).catch(() => toast.error("Failed to generate sheet"));
                    }}
                    className="text-teal-700 hover:text-teal-800 hover:bg-teal-50 border-teal-300"
                    data-testid="download-sheet-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Sheet
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Timer Display (Mobile) */}
            <div className="md:hidden flex justify-center">
              <div className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold text-lg ${timeExceeded ? 'bg-red-100 text-red-700' : timeLeft <= 60 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                <Timer className="w-6 h-6" />
                {formatTime(timeLeft)}
              </div>
            </div>

            {/* Upload Section — 1 page slot */}
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="w-5 h-5 text-blue-600" />
                  Upload Your Answer
                </CardTitle>
                <p className="text-gray-500 text-sm">Take a clear, well-lit photo of your answer</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Single Page Upload Slot */}
                {[
                  { label: "UPLOAD YOUR ANSWER", required: true }
                ].map(({ label, required }, idx) => (
                  <div key={idx} className={`relative rounded-xl border-2 border-dashed transition-all ${pages[idx] ? 'border-green-400 bg-green-50' : required ? 'border-blue-300 bg-blue-50/40 hover:border-blue-500' : 'border-gray-200 bg-gray-50/40 hover:border-gray-400'}`}>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf"
                      onChange={(e) => handlePageUpload(e.target.files[0], idx)}
                      className="hidden"
                      id={`page-${idx + 1}-upload`}
                      data-testid={`page-${idx + 1}-upload`}
                    />
                    <label htmlFor={`page-${idx + 1}-upload`} className="flex items-center gap-4 p-4 cursor-pointer">
                      {/* Left: thumb or icon */}
                      <div className={`w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0 ${pages[idx] ? 'bg-white shadow' : 'bg-white/60'}`}>
                        {pages[idx] ? (
                          pagePreviews[idx] && pagePreviews[idx] !== 'pdf' ? (
                            <img src={pagePreviews[idx]} alt={`Page ${idx + 1}`} className="w-16 h-16 object-cover rounded-lg" />
                          ) : (
                            <FileText className="w-8 h-8 text-green-600" />
                          )
                        ) : (
                          <Upload className={`w-7 h-7 ${required ? 'text-blue-400' : 'text-gray-300'}`} />
                        )}
                      </div>
                      {/* Right: text */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-bold text-sm tracking-wide ${pages[idx] ? 'text-green-700' : required ? 'text-blue-700' : 'text-gray-500'}`}>
                            {label}
                          </span>
                          {required && !pages[idx] && (
                            <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">MANDATORY</span>
                          )}
                          {!required && !pages[idx] && (
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Optional</span>
                          )}
                          {pages[idx] && (
                            <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full font-medium">Uploaded</span>
                          )}
                        </div>
                        {pages[idx] ? (
                          <p className="text-xs text-gray-500 truncate mt-0.5">{pages[idx].name} • {(pages[idx].size / 1024 / 1024).toFixed(1)} MB</p>
                        ) : (
                          <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, WEBP, HEIC or PDF • Max 10MB</p>
                        )}
                      </div>
                    </label>
                    {/* Remove button */}
                    {pages[idx] && (
                      <button
                        onClick={(e) => { e.preventDefault(); handleRemovePage(idx); }}
                        className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-600 rounded-full transition-colors"
                        data-testid={`remove-page-${idx + 1}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}

                {/* Submit Button */}
                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={handleSubmit}
                    className="flex-1 h-14 text-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    disabled={!pages[0]}
                    data-testid="submit-answer"
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Submit for Evaluation
                  </Button>
                  
                  {/* Shuffle button — disabled once files uploaded to prevent accidental question change */}
                  <Button
                    onClick={() => {
                      if (pages.filter(Boolean).length > 0) return; // safety guard
                      handleShuffleQuestion();
                    }}
                    variant="outline"
                    className={`h-14 px-6 border-2 ${pages.filter(Boolean).length > 0 ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-blue-500 text-blue-600 hover:bg-blue-50'}`}
                    disabled={shuffling || pages.filter(Boolean).length > 0}
                    title={pages.filter(Boolean).length > 0 ? "Remove uploaded files to change question" : "Get a different question"}
                    data-testid="shuffle-question"
                  >
                    <RefreshCw className={`w-5 h-5 ${shuffling ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                
                {pages.filter(Boolean).length > 0 && (
                  <p className="text-xs text-center text-gray-500">
                    Answer ready — AI will evaluate your response
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 6: Evaluating with Interesting Facts */}
        {step === 6 && (
          <Card className="shadow-xl overflow-hidden">
            <CardContent className="py-12 text-center">
              <div className="mb-6">
                <div className="relative inline-block">
                  <Loader2 className="w-16 h-16 animate-spin text-purple-600 mx-auto" />
                  <Sparkles className="w-6 h-6 text-yellow-500 absolute -top-1 -right-1 animate-pulse" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">CBSE Expert Team is Evaluating</h2>
              <p className="text-gray-600 mb-4">Analyzing your answer... Please wait.</p>
              
              {/* Interesting Fact Display */}
              {currentFact && (
                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-xl p-5 mx-auto max-w-lg mt-6">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-left">
                      <p className="text-sm font-semibold text-amber-700 mb-1">Did You Know?</p>
                      <p className="text-gray-700 text-sm leading-relaxed">{currentFact}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 7: Result */}
        {step === 7 && result && (
          <div className="space-y-6">
            {/* Score Card */}
            <Card className="shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 text-center">
                <h2 className="text-lg opacity-90 mb-2">Your Score</h2>
                <div className="text-6xl font-bold mb-2">{result.score}</div>
                <div className="text-xl opacity-90">out of {result.total_marks}</div>
                {result.time_penalty_applied && (
                  <Badge className="mt-3 bg-red-500">-1 Time Penalty Applied</Badge>
                )}
              </div>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-blue-600">#{result.rank}</div>
                    <div className="text-sm text-gray-600">Rank</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-purple-600">{result.percentile}%</div>
                    <div className="text-sm text-gray-600">Percentile</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Question Recap */}
            <Card className="shadow-lg">
              <CardHeader className="bg-gray-50">
                <CardTitle className="text-lg">Question</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <p className="text-gray-700">{question}</p>
              </CardContent>
            </Card>

            {/* Extracted Answer */}
            <Card className="shadow-lg border-2 border-blue-200">
              <CardHeader className="bg-blue-50">
                <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
                  <FileText className="w-5 h-5" />
                  What Our System Read From Your Answer
                </CardTitle>
                <p className="text-xs text-blue-600">हमारे AI ने आपकी उत्तर-पुस्तिका से यह पढ़ा — verify करें कि यह आपके लिखे से मेल खाता है</p>
              </CardHeader>
              <CardContent className="p-4">
                <div className="bg-white rounded border border-blue-100 p-3 text-gray-700 text-sm leading-relaxed space-y-1 max-h-60 overflow-y-auto">
                  {result.ocr_failed || (result.extracted_text && result.extracted_text.startsWith('OCR_FAILED'))
                    ? <div className="text-red-600 text-sm space-y-2">
                        <p className="font-semibold">Image could not be read. Evaluation was not performed.</p>
                        <p>Please ensure your photo is:</p>
                        <ul className="list-disc ml-4 space-y-1">
                          <li>Well-lit (natural light or bright indoor light)</li>
                          <li>Focused and not blurry</li>
                          <li>Handwriting is clear and legible</li>
                          <li>Full page is visible in frame</li>
                        </ul>
                      </div>
                    : result.extracted_text && !result.extracted_text.includes('OCR failed') && !result.extracted_text.includes('Unable to extract')
                    ? result.extracted_text.split('\n').map((line, i) => (
                        <div key={i} className={line.trim() === '' ? 'h-2' : ''}>
                          {line.trim() !== '' && <MathText text={line} />}
                        </div>
                      ))
                    : result.extracted_text && (result.extracted_text.includes('OCR failed') || result.extracted_text.includes('Unable to extract'))
                    ? <div className="text-orange-600 italic text-sm">
                        <p className="font-medium mb-1">Image could not be read clearly.</p>
                        <p>Please ensure: image is clear, well-lit, and text is legible. Try uploading a higher quality photo.</p>
                      </div>
                    : <span className="text-gray-400 italic">No text extracted — evaluation was done without OCR</span>
                  }
                </div>
              </CardContent>
            </Card>

            {/* Feedback */}
            <Card className="shadow-lg">
              <CardHeader className="bg-gray-50">
                <CardTitle className="text-lg">CBSE Expert Team Feedback</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <FeedbackCard feedback={result.feedback} timePenaltyApplied={result.time_penalty_applied} />
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleNewQuestion}
                className="flex-1 h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                data-testid="new-question"
              >
                Practice Another Question
              </Button>
              <Link to="/dashboard" className="flex-1">
                <Button variant="outline" className="w-full h-12">
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        )}
      </main>
      
      {/* Floating Back Button */}
      <FloatingBackButton 
        onBack={
          step === 5 ? () => setStep(4) :
          step === 4 ? () => setStep(3) :
          step === 3 ? () => setStep(2) :
          step === 2 ? () => setStep(1) :
          topic ? () => { setTopic(""); setStep(1); } :
          subject ? () => { setSubject(""); setStep(1); } :
          examType ? () => { setExamType(""); setStep(1); } :
          null
        }
        showHome={true}
      />
      
      {/* Premium Popup for locked content */}
      {showPremiumPopup && (
        <PremiumPopup
          user={user}
          onClose={() => setShowPremiumPopup(false)}
          onUpgrade={onLogout}
        />
      )}
    </div>
  );
}
